import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { EnhancedDialog } from 'components/enhanced/dialog';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { MonthPickerAdapter } from 'components/enhanced/form/adapters/date-time/MonthPickerAdapter';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
// Definizione delle modalità supportate
type PlanningMode = 'MONTH_ONLY' | 'SPECIFIC_DATES' | 'RECURRENCE';

interface MaintenancePlannerProps {
  onSave: (payload: { mode: PlanningMode; dates: string[]; meta?: any }) => void;
  open: boolean;
  onClose: () => void;
}

export const AdvancedMaintenancePlanner = ({ onSave, open, onClose }: MaintenancePlannerProps) => {
  const { t } = useTranslation();
  // Stato della modalità principale
  const [planningMode, setPlanningMode] = useState<PlanningMode>('MONTH_ONLY');

  // Stati per la modalità "Solo Mese"
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format('YYYY-MM'));

  // Stati per la modalità "Date Specifiche"
  const [specificDateInput, setSpecificDateInput] = useState<string>('');
  const [specificDatesList, setSpecificDatesList] = useState<string[]>([]);

  // Stati per la modalità "Frequenza"
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [frequencyValue, setFrequencyValue] = useState<number>(1);
  const [frequencyUnit, setFrequencyUnit] = useState<'day' | 'week' | 'month'>('month');
  const [occurrencesCount, setOccurrencesCount] = useState<number>(5);

  // Elenco finale delle date calcolate/selezionate da inviare al backend
  const [calculatedDates, setCalculatedDates] = useState<string[]>([]);

  const generateRecurrentDates = useCallback(() => {
    if (!startDate || frequencyValue <= 0 || occurrencesCount <= 0) return;

    const generated: string[] = [];
    let current = dayjs(startDate);

    for (let i = 0; i < occurrencesCount; i++) {
      generated.push(current.format('YYYY-MM-DD'));
      current = current.add(frequencyValue, frequencyUnit);
    }
    setCalculatedDates(generated);
  }, [startDate, frequencyValue, frequencyUnit, occurrencesCount]);

  useEffect(() => {
    if (planningMode === 'MONTH_ONLY') {
      setCalculatedDates([dayjs(selectedMonth).startOf('month').format('YYYY-MM-DD')]);
    } else if (planningMode === 'SPECIFIC_DATES') {
      setCalculatedDates([...specificDatesList].sort());
    } else if (planningMode === 'RECURRENCE') {
      generateRecurrentDates();
    }
  }, [planningMode, selectedMonth, specificDatesList, generateRecurrentDates]);

  const handleAddSpecificDate = () => {
    if (specificDateInput && !specificDatesList.includes(specificDateInput)) {
      setSpecificDatesList((prev) => [...prev, specificDateInput]);
      setSpecificDateInput('');
    }
  };

  const handleRemoveSpecificDate = (dateToRemove: string) => {
    setSpecificDatesList((prev) => prev.filter((d) => d !== dateToRemove));
  };

  const handleFormValuesChange = useCallback(
    (formState: any) => {
      const { values } = formState;

      if (planningMode === 'MONTH_ONLY') {
        const selectedMonth = values.month ? dayjs(values.month) : dayjs();
        setCalculatedDates([selectedMonth.startOf('month').format('YYYY-MM-DD')]);
      } else if (planningMode === 'SPECIFIC_DATES') {
        setCalculatedDates([...specificDatesList]);
      } else if (planningMode === 'RECURRENCE') {
        const start = values.startDate || dayjs().format('YYYY-MM-DD');
        const freqValue = parseInt(values.frequencyValue) || 1;
        const freqUnit = values.frequencyUnit || 'month';
        const count = parseInt(values.occurrencesCount) || 5;

        const generated: string[] = [];
        let current = dayjs(start);

        for (let i = 0; i < count; i++) {
          generated.push(current.format('YYYY-MM-DD'));
          current = current.add(freqValue, freqUnit);
        }
        setCalculatedDates(generated);
      }
    },
    [planningMode, specificDatesList]
  );

  const onSubmit = (values: any) => {
    let metaPayload: any = null;

    if (planningMode === 'RECURRENCE') {
      metaPayload = {
        startDate: values.startDate || dayjs().format('YYYY-MM-DD'),
        frequencyValue: parseInt(values.frequencyValue) || 1,
        frequencyUnit: values.frequencyUnit || 'month',
        occurrencesCount: parseInt(values.occurrencesCount) || 5
      };
    }

    onSave({
      mode: planningMode,
      dates: calculatedDates,
      meta: metaPayload
    });
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      title={planningMode === 'MONTH_ONLY' ? 'Nuova Manutenzione' : 'Nuove Manutenzioni'}
      subTitle={'Seleziona la modalità di inserimento delle tempistiche di intervento.'}
      submitLabel={'Salva'}
      submitButtonProps={{
        type: 'submit',
        form: 'programmedMaintenanceForm'
      }}>
      {/* Selettore della modalità */}
      <RadioGroup
        value={planningMode}
        onChange={(e) => {
          setPlanningMode(e.target.value as PlanningMode);
          setCalculatedDates([]);
        }}
        sx={{ mb: 1, ml: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 3 }}
          justifyContent="space-between">
          <FormControlLabel
            value="MONTH_ONLY"
            control={<Radio />}
            label={
              <Typography variant="body2" fontWeight={500}>
                Mese Indicativo
              </Typography>
            }
          />
          <FormControlLabel
            value="SPECIFIC_DATES"
            control={<Radio />}
            label={
              <Typography variant="body2" fontWeight={500}>
                Date specifiche
              </Typography>
            }
          />
          <FormControlLabel
            value="RECURRENCE"
            control={<Radio />}
            label={
              <Typography variant="body2" fontWeight={500}>
                Frequenza programmata
              </Typography>
            }
          />
        </Stack>
      </RadioGroup>

      <Divider sx={{ my: 2 }} />

      {/* 1. SEZIONE: SOLO MESE */}
      <EnhancedForm onSubmit={onSubmit} formId="programmedMaintenanceForm" hideControls={true}>
        <FormSpy subscription={{ values: true }} onChange={handleFormValuesChange} />

        {planningMode === 'MONTH_ONLY' && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Scegli il mese previsto per l&apos;intervento:
            </Typography>
            <IdentityField
              name="month"
              value={selectedMonth}
              component={MonthPickerAdapter}
              label={t('global.month')}
              required={true}
            />
          </Box>
        )}

        {/* 2. SEZIONE: DATE SPECIFICHE */}
        {planningMode === 'SPECIFIC_DATES' && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Aggiungi manualmente i giorni esatti delle manutenzioni:
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                type="date"
                value={specificDateInput}
                onChange={(e) => setSpecificDateInput(e.target.value)}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleAddSpecificDate}
                disabled={!specificDateInput}>
                Aggiungi
              </Button>
            </Stack>

            {specificDatesList.length > 0 && (
              <List
                dense
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  maxHeight: 150,
                  overflow: 'auto'
                }}>
                {specificDatesList.map((date) => (
                  <ListItem
                    key={date}
                    secondaryAction={
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveSpecificDate(date)}>
                        Rimuovi
                      </Button>
                    }>
                    <ListItemText primary={dayjs(date).format('DD/MM/YYYY')} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* 3. SEZIONE: FREQUENZA E RICORRENZA */}
        {planningMode === 'RECURRENCE' && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Configura i parametri per il calcolo automatico delle scadenze:
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label="Data del primo intervento"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Ogni quanti"
                  type="number"
                  value={frequencyValue}
                  onChange={(e) => setFrequencyValue(Math.max(1, parseInt(e.target.value) || 1))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  label="Unità di tempo"
                  value={frequencyUnit}
                  onChange={(e) => setFrequencyUnit(e.target.value as any)}
                  sx={{ flex: 1 }}>
                  <MenuItem value="day">Giorni</MenuItem>
                  <MenuItem value="week">Settimane</MenuItem>
                  <MenuItem value="month">Mesi</MenuItem>
                </TextField>
              </Stack>

              <TextField
                label="Numero totale di interventi da pianificare"
                type="number"
                value={occurrencesCount}
                onChange={(e) => setOccurrencesCount(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
              />
            </Stack>
          </Box>
        )}

        {/* ANTEPRIMA DELLE DATE CALCOLATE */}
        {calculatedDates.length > 0 && (
          <Box
            sx={{
              mt: 4,
              p: 2.5,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'primary.main'
            }}>
            <Typography
              variant="caption"
              color="primary.main"
              fontWeight={700}
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                mb: 0.5
              }}>
              Date pianificate risultanti ({calculatedDates.length})
            </Typography>

            <Typography
              variant="body2"
              color="text.primary"
              fontWeight={500}
              sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
              {planningMode === 'MONTH_ONLY'
                ? calculatedDates.map((d) => dayjs(d).format('MM/YYYY')).join(', ')
                : calculatedDates.map((d) => dayjs(d).format('DD/MM/YYYY')).join(', ')}
            </Typography>
          </Box>
        )}
      </EnhancedForm>
    </EnhancedDialog>
  );
};
